import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import { useCulturesAlerts } from "../context/CulturesAlertContext";

// Sigla corta de cama/tipo si el nombre del paciente la trae; si no, iniciales.
const shortWho = (a) => {
  const n = String(a?.patientName || "").trim();
  if (n) return n.length > 26 ? n.slice(0, 24) + "…" : n;
  return `RUT ${a?.rut || "—"}`;
};

const statusColor = (st) =>
  st === "high" ? "var(--color-danger)" : "var(--color-warning)";

/**
 * Popup global de cultivos nuevos. Montado a nivel app (junto a KwDetectorTool)
 * para que aparezca en cualquier vista cuando una descarga detecta cultivos.
 */
export default function CulturesAlertPopup() {
  const { popupBatch, dismissPopup, markSeen } = useCulturesAlerts();
  const navigate = useNavigate();

  const batch = popupBatch || [];
  const open = batch.length > 0;
  const many = batch.length > 1;
  const firstRut = batch[0]?.rut;

  const handleView = () => {
    // Marcar como vistos los del popup y navegar al visualizador del primero.
    batch.forEach((a) => markSeen(a.id));
    dismissPopup();
    if (firstRut) {
      navigate(`/visualizador-examenes?rut=${encodeURIComponent(firstRut)}`);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={dismissPopup}
      maxWidth="xs"
      fullWidth
      // El visualizador de exámenes es un Dialog (viewer, z-index modal=1300).
      // Este popup se abre en el mismo tick que el viewer, así que subimos el
      // z-index explícito para garantizar que quede SIEMPRE encima y no dependa
      // del orden de montaje de los portales.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 50 }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0,0,0,0.7)", backdropFilter: "blur(3px)" },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: "var(--bg-elevated)",
          backgroundImage: "none",
          border: "1px solid rgba(var(--color-danger-rgb),0.35)",
          boxShadow: "0 22px 55px rgba(var(--shadow-rgb),0.6)",
        },
      }}
    >
      <DialogContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <BiotechOutlinedIcon sx={{ color: "var(--color-danger)", fontSize: 22 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: "var(--text-primary)" }}>
            {many ? "Cultivos nuevos" : "Cultivo nuevo"}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: "var(--text-muted)", mb: 1.5 }}>
          <Box component="span" sx={{ color: "var(--color-success)", fontWeight: 600 }}>
            ✓ Exámenes actualizados.
          </Box>{" "}
          {many
            ? `Aparecieron ${batch.length} cultivos que vale la pena revisar:`
            : "Apareció un cultivo que vale la pena revisar:"}
        </Typography>

        <Stack spacing={0.75} sx={{ mb: 2 }}>
          {batch.slice(0, 6).map((a) => (
            <Stack
              key={a.id}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                p: "8px 10px",
                borderRadius: "9px",
                backgroundColor: "rgba(var(--color-danger-rgb),0.08)",
                border: "1px solid rgba(var(--color-danger-rgb),0.22)",
              }}
            >
              {a.abbrev && (
                <Box
                  sx={{
                    fontFamily: "monospace",
                    fontSize: 9,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    backgroundColor: "rgba(var(--fg-rgb),0.08)",
                    px: 0.75,
                    py: 0.25,
                    borderRadius: "4px",
                    flexShrink: 0,
                  }}
                >
                  {a.abbrev}
                </Box>
              )}
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {shortWho(a)}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontSize: 11,
                  fontWeight: 700,
                  color: statusColor(a.status),
                  flexShrink: 0,
                }}
              >
                {a.status === "warning" ? "en curso" : a.germen || "positivo"}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            fullWidth
            onClick={dismissPopup}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "var(--text-muted)",
              backgroundColor: "rgba(var(--fg-rgb),0.06)",
              "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.1)" },
            }}
          >
            Después
          </Button>
          <Button
            fullWidth
            onClick={handleView}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "var(--text-primary)",
              backgroundColor: "var(--color-primary)",
              "&:hover": { backgroundColor: "var(--color-primary-soft)" },
            }}
          >
            Ver exámenes
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
